import React, { useRef, useEffect } from 'react'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import { Img } from 'openimg/react'

interface PreloaderProps {
  onComplete?: () => void
}

const preloaderImages = [
  '/img/preloader/5C7A5622.webp',
  '/img/preloader/5C7A5668.webp',
  '/img/preloader/5C7A5676.webp',
  '/img/preloader/5C7A5707.webp',
  '/img/preloader/5C7A5710.webp',
  '/img/preloader/5C7A5726.webp',
  '/img/preloader/5C7A5907.webp',
  '/img/preloader/5C7A6024.webp',
]

export default function Preloader({ onComplete }: PreloaderProps) {
  const counterRef = useRef<HTMLSpanElement>(null)
  const imageRefs = useRef<(HTMLDivElement | null)[]>([])
  const progressBarRef = useRef<HTMLDivElement>(null)
  const overlayRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  useGSAP(() => {
    const tl = gsap.timeline({
      onComplete: () => {
        if (onComplete) {
          // Create exit animation timeline
          const exitTl = gsap.timeline({
            onComplete
          })

          // Fade out the counter and progress bar
          exitTl.to([counterRef.current, progressBarRef.current], {
            opacity: 0,
            duration: 0.5,
            ease: "power2.inOut"
          })

          // Move content up and fade out background
          exitTl.to(contentRef.current, {
            yPercent: -100,
            duration: 1,
            ease: "power2.inOut"
          }, "-=0.3")

          // Fade out the overlay background
          exitTl.to(overlayRef.current, {
            backgroundColor: "transparent",
            duration: 0.8,
            ease: "power2.inOut"
          }, "-=0.8")
        }
      }
    })

    // Counter animation
    tl.to(counterRef.current, {
      innerText: 100,
      duration: 10,
      snap: { innerText: 1 },
      ease: "none"
    })

    // Progress bar animation
    tl.to(progressBarRef.current, {
      width: "100%",
      duration: 10,
      ease: "none"
    }, 0)

    // Image animations
    imageRefs.current.forEach((img, index) => {
      const startPositions = [
        { x: "-100vw", y: "100vh" },  // bottom left
        { x: "100vw", y: "-100vh" },  // top right
        { x: "100vw", y: "0" },       // right
        { x: "-100vw", y: "-100vh" }, // top left
        { x: "0", y: "100vh" },       // bottom
        { x: "100vw", y: "100vh" },   // bottom right
        { x: "-100vw", y: "0" },      // left
        { x: "0", y: "-100vh" }       // top
      ] as const;

      const defaultStart = { x: "-100vw", y: "-100vh" };
      const start = startPositions[index] || defaultStart;

      if (!img) return;

      gsap.set(img, {
        x: start.x,
        y: start.y,
        opacity: 0,
        rotate: gsap.utils.random(-180, 180)
      });

      tl.to(img, {
        x: 0,
        y: 0,
        opacity: 1,
        rotate: 0,
        duration: 3,
        ease: "power2.out"
      }, index * 0.2 + 2)
    })
  }, [onComplete])

  return (
    <div ref={overlayRef} className="fixed inset-0 z-50 bg-black">
      <div ref={contentRef}>
        {/* Images Grid */}
        <div className="absolute inset-0 grid grid-cols-4 grid-rows-2 gap-4 p-6">
          {preloaderImages.map((src, index) => (
            <div
              key={index}
              ref={el => imageRefs.current[index] = el}
              className="relative overflow-hidden rounded-lg bg-gray-900"
            >
              <Img
                width={100}
                height={100}
                src={src}
                alt={`7even Sports ${index + 1}`}
                className="h-full w-full object-cover"
              />
            </div>
          ))}
        </div>

        {/* Loading Counter */}
        <div className="absolute bottom-12 right-12 z-20 flex items-baseline">
          <span ref={counterRef} className="text-8xl font-black text-white">0</span>
          <span className="text-4xl font-bold text-white ml-2">%</span>
        </div>

        {/* Progress Bar */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-800">
          <div
            ref={progressBarRef}
            className="h-full w-0 bg-white"
          />
        </div>
      </div>
    </div>
  )
} 